import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Avatar,
  DialogActions,
  Badge,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import { Add, PictureAsPdf, Send, Edit, Approval as ApprovalIcon, Draw as DocuSignIcon, HistoryEdu as EsignIcon, Delete as DeleteIcon, CheckCircle as CheckCircleIcon, ContentCopy as ContentCopyIcon, Close as CloseIcon } from '@mui/icons-material';
import { createApiUrl, makeApiCall } from '../../config/api';
import { formatCurrencyWithSymbol } from '../../utils/currencyUtils';
import { triggerSignedUrlDownload } from '../../utils/download';
import { useAuth } from '../../contexts/AuthContext';
import QuoteForm from './QuoteForm';
import QuoteTypeSelector from './QuoteTypeSelector';
import UniversalQuoteForm from './UniversalQuoteForm';
import QuoteApprovalDialog from './QuoteApprovalDialog';
import SendForSignatureDialog from '../Esign/SendForSignatureDialog';
import EnvelopeDetailDialog from '../Esign/EnvelopeDetailDialog';
import { esignStatusColor } from '../Esign/esignUtils';

const QuoteList = ({ initialStatus = '' }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState(initialStatus);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedQuoteType, setSelectedQuoteType] = useState(null);
  const [editQuote, setEditQuote] = useState(null);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [siteDiagramModalOpen, setSiteDiagramModalOpen] = useState(false);
  const [selectedSiteDiagram, setSelectedSiteDiagram] = useState(null);
  const [docuSignDialogOpen, setDocuSignDialogOpen] = useState(false);
  const [docuSignLoading, setDocuSignLoading] = useState(false);
  const [docuSignQuote, setDocuSignQuote] = useState(null);
  // TKT-401: in-house e-signature, offered next to DocuSign.
  const [esignQuote, setEsignQuote] = useState(null);
  const [esignDetailId, setEsignDetailId] = useState(null);
  const [signingProvider, setSigningProvider] = useState('docusign');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [quoteToDuplicate, setQuoteToDuplicate] = useState(null);
  const [duplicating, setDuplicating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [emailDialog, setEmailDialog] = useState({ open: false, quote: null, to: '', sending: false });
  const [pdfGenerating, setPdfGenerating] = useState(null); // Tracks which quote ID is generating PDF
  const [pdfProgress, setPdfProgress] = useState(''); // Progress message

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(limit),
        ...(search ? { search } : {}),
        ...(status ? { status } : {})
      });
      const res = await makeApiCall(`api/quotes?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.loading'));
      setQuotes(data.quotes || []);
      setTotal(data.pagination?.total || 0);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchQuotes(); }, [page, limit, status]);

  const openQuoteApprovalById = async (quoteId) => {
    try {
      const res = await makeApiCall(`api/quotes/${quoteId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load quote');
      setSelectedQuote(data.quote);
      setApprovalDialogOpen(true);
    } catch (e) {
      console.error('Failed to open quote approval dialog:', e);
      setSnackbar({
        open: true,
        message: e.message || t('quotes.failedToLoadQuote', 'Failed to load quote'),
        severity: 'error'
      });
    }
  };

  // Auto-open quote actions if we arrived via dashboard/navigation state.
  // Quote approval notifications set selectedQuoteAction='approval' so the
  // shortcut opens the approval modal directly instead of the edit form.
  useEffect(() => {
    const pendingId = sessionStorage.getItem('selectedQuoteId');
    if (!pendingId) return;
    sessionStorage.removeItem('selectedQuoteId');
    const pendingAction = sessionStorage.getItem('selectedQuoteAction');
    sessionStorage.removeItem('selectedQuoteAction');
    if (pendingAction === 'approval') {
      openQuoteApprovalById(pendingId);
    } else {
      handleEdit({ id: pendingId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateSuccess = () => {
    setCreateOpen(false);
    setSelectedQuoteType(null);
    fetchQuotes();
  };

  const handleApprovalSuccess = (message, updatedQuote) => {
    setApprovalDialogOpen(false);
    setSelectedQuote(null);
    fetchQuotes();
    // Could show success message here
  };

  const handleApprovalClick = (quote) => {
    setSelectedQuote(quote);
    setApprovalDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'default';
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'expired': return 'default';
      case 'sent_for_signature': return 'info';
      case 'declined': return 'error';
      default: return 'default';
    }
  };

  const getEnvelopeStatusColor = (envelopeStatus) => {
    switch (envelopeStatus) {
      case 'sent': return 'info';
      case 'delivered': return 'info';
      case 'completed': return 'success';
      case 'declined': return 'error';
      case 'voided': return 'default';
      default: return 'default';
    }
  };

  const handleQuoteTypeSelect = (type) => {
    setSelectedQuoteType(type);
  };

  const handleCreateCancel = () => {
    setCreateOpen(false);
    setSelectedQuoteType(null);
  };

  const keepQuoteDialogOpen = (reason) => (
    reason === 'backdropClick' || reason === 'escapeKeyDown'
  );

  const handleEdit = async (q) => {
    try {
      const res = await makeApiCall(`api/quotes/${q.id}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load quote');
      setEditQuote({ 
        ...data.quote, 
        items: data.items,
        service_configs: data.service_configs || []
      });
    } catch (e) {
      // noop
    }
  };

  // Generate a fresh PDF for the quote and wait for the background job to
  // finish. Resolves with the job result ({ signed_url, ... }).
  const generatePdfAndWait = async (q) => {
    setPdfGenerating(q.id);
    setPdfProgress('Starting PDF generation...');
    try {
      // Start PDF generation (returns job ID immediately)
      const generateRes = await makeApiCall(`api/quotes/${q.id}/generate-pdf`, { method: 'POST' });
      const generateData = await generateRes.json();

      if (!generateData.jobId) {
        // Fallback for old API that returns signed_url directly
        if (generateData.signed_url) return generateData;
        throw new Error('No job ID returned');
      }

      const maxAttempts = 120; // 2 minutes with 1 second intervals
      for (let attempts = 0; attempts < maxAttempts; attempts += 1) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const statusRes = await makeApiCall(`api/quotes/pdf-status/${generateData.jobId}`);
        const statusData = await statusRes.json();

        if (statusData.status === 'completed' && statusData.result?.signed_url) {
          // Show the row as having a PDF straight away; the list isn't
          // reloaded after a download, and Send for signature checks this.
          setQuotes((prev) => prev.map((row) => (row.id === q.id ? { ...row, has_pdf: true } : row)));
          return statusData.result;
        }
        if (statusData.status === 'failed') {
          throw new Error(statusData.error || 'PDF generation failed');
        }
        // Still processing - update progress message and continue polling
        setPdfProgress(statusData.message || 'Generating PDF...');
      }
      throw new Error('PDF generation timed out. Please try again.');
    } finally {
      setPdfGenerating(null);
      setPdfProgress('');
    }
  };

  const handleGeneratePdf = async (q) => {
    try {
      const result = await generatePdfAndWait(q);
      // TKT-233: the signed URL is served with attachment disposition,
      // so a same-tab anchor click downloads in place — no new tab.
      triggerSignedUrlDownload(result.signed_url);
    } catch (e) {
      console.error('Error generating PDF:', e);
      alert(e.message || 'Failed to generate PDF. Please try again.');
    }
  };

  // QL-035: "Email Quote" used to post and ignore the answer, and the server
  // could never find the PDF, so nothing was ever sent. It now confirms the
  // recipient, generates a fresh PDF (so edits since the last PDF are
  // included), emails it and reports the result.
  const handleEmail = (q) => {
    setEmailDialog({ open: true, quote: q, to: q.email_address || '', sending: false });
  };

  const handleSendEmail = async () => {
    const { quote, to } = emailDialog;
    if (!quote) return;
    if (!to.trim()) {
      setSnackbar({ open: true, message: t('quotes.emailDialog.noRecipient'), severity: 'error' });
      return;
    }
    setEmailDialog((prev) => ({ ...prev, sending: true }));
    try {
      await generatePdfAndWait(quote);
      const res = await makeApiCall(`api/quotes/${quote.id}/email`, {
        method: 'POST',
        body: JSON.stringify({ to_email: to.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || t('quotes.emailDialog.failed'));
      setEmailDialog({ open: false, quote: null, to: '', sending: false });
      setSnackbar({ open: true, message: t('quotes.emailDialog.sent', { email: data.to || to.trim() }), severity: 'success' });
    } catch (e) {
      console.error('Error emailing quote:', e);
      setEmailDialog((prev) => ({ ...prev, sending: false }));
      setSnackbar({ open: true, message: `${t('quotes.emailDialog.failed')} ${e.message || ''}`.trim(), severity: 'error' });
    }
  };

  const handleDocuSignClick = (quote) => {
    setDocuSignQuote(quote);
    setDocuSignDialogOpen(true);
  };

  const handleDeleteClick = (quote) => {
    setQuoteToDelete(quote);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!quoteToDelete) return;
    
    setDeleting(true);
    try {
      const res = await makeApiCall(`api/quotes/${quoteToDelete.id}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        setDeleteDialogOpen(false);
        setSnackbar({
          open: true,
          message: t('quotes.deleteSuccess', { number: quoteToDelete.quote_number }),
          severity: 'success',
        });
        setQuoteToDelete(null);
        fetchQuotes(); // Refresh the list
      } else {
        // TKT-460: say why, in the app's own message instead of a browser
        // pop-up.
        const data = await res.json().catch(() => ({}));
        let message = t('quotes.deleteFailed');
        if (data.code === 'QUOTE_ON_ROUTE') {
          const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          const routes = (data.routes || [])
            .map((r) => {
              // Day 0 is the truck's season snow list (TKT-443).
              const dayLabel = Number(r.day_of_week) === 0
                ? t('routeSetup.snowListColumn')
                : (dayKeys[r.day_of_week - 1] ? t(`days.${dayKeys[r.day_of_week - 1]}`) : r.day_of_week);
              return `${r.truck_name || '?'} (${dayLabel})`;
            })
            .join(', ');
          message = t('quotes.deleteOnRoute', { routes });
        } else if (data.code === 'QUOTE_HAS_WORK') {
          message = t('quotes.deleteHasWork', { count: data.worked_visits || 0 });
        }
        setSnackbar({ open: true, message, severity: 'error' });
      }
    } catch (e) {
      console.error('Error deleting quote:', e);
      setSnackbar({ open: true, message: t('quotes.deleteFailed'), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setQuoteToDelete(null);
  };

  const handleDuplicateClick = (quote) => {
    setQuoteToDuplicate(quote);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateCancel = () => {
    if (duplicating) return;
    setDuplicateDialogOpen(false);
    setQuoteToDuplicate(null);
  };

  const handleDuplicateConfirm = async () => {
    if (!quoteToDuplicate) return;
    setDuplicating(true);
    try {
      const res = await makeApiCall(`api/quotes/${quoteToDuplicate.id}/duplicate`, {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to duplicate quote');
      }
      const newQuoteNumber = data?.quote?.quote_number || '';
      setSnackbar({
        open: true,
        message: t('quotes.duplicateSuccess', { quoteNumber: newQuoteNumber }),
        severity: 'success'
      });
      setDuplicateDialogOpen(false);
      setQuoteToDuplicate(null);
      fetchQuotes();
    } catch (e) {
      console.error('Error duplicating quote:', e);
      setSnackbar({
        open: true,
        message: e.message || t('quotes.duplicateError'),
        severity: 'error'
      });
    } finally {
      setDuplicating(false);
    }
  };

  const handleSnackbarClose = (_event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDocuSignSend = async () => {
    if (!docuSignQuote) return;
    
    setDocuSignLoading(true);
    try {
      const res = await makeApiCall(`api/docusign/send-quote/${docuSignQuote.id}`, {
        method: 'POST',
        body: JSON.stringify({
          // Optional: override recipient details if needed
          // recipientEmail: '',
          // recipientName: '',
        })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to send quote via DocuSign');
      }
      
      alert(`Quote sent via DocuSign successfully!\nEnvelope ID: ${data.envelopeId}\nRecipient: ${data.recipient}`);
      setDocuSignDialogOpen(false);
      setDocuSignQuote(null);
      fetchQuotes(); // Refresh the list
    } catch (error) {
      console.error('DocuSign error:', error);
      alert(`Failed to send quote via DocuSign: ${error.message}`);
    } finally {
      setDocuSignLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await makeApiCall('api/esign/settings');
        if (res.ok) setSigningProvider((await res.json()).defaultProvider || 'docusign');
      } catch {
        // Keep DocuSign highlighted.
      }
    })();
  }, []);

  const handleEsignSent = ({ message, severity }) => {
    setEsignQuote(null);
    setSnackbar({ open: true, message, severity });
    fetchQuotes();
  };

  const handleSiteDiagramClick = (quote) => {
    if (quote.site_diagram_signed_url) {
      setSelectedSiteDiagram({
        url: quote.site_diagram_signed_url,
        filename: quote.site_diagram_filename || 'Site Diagram',
        quoteNumber: quote.quote_number
      });
      setSiteDiagramModalOpen(true);
    }
  };

  return (
    <Box data-demo-target="page-quotes-root">
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">{t('quotes.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)} data-demo-target="page-quotes-create">{t('quotes.createQuote')}</Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            label={t('common.search')}
            placeholder={t('quotes.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setPage(0); fetchQuotes(); }}}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="status-label">{t('quotes.status')}</InputLabel>
            <Select labelId="status-label" label={t('quotes.status')} value={status} onChange={e => { setStatus(e.target.value); setPage(0); }}>
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="draft">{t('quotes.draft')}</MenuItem>
              <MenuItem value="pending">{t('quotes.pending')}</MenuItem>
              <MenuItem value="approved">{t('quotes.approved')}</MenuItem>
              <MenuItem value="rejected">{t('quotes.rejected')}</MenuItem>
              <MenuItem value="sent_for_signature">{t('quotes.sent_for_signature')}</MenuItem>
              <MenuItem value="declined">{t('quotes.declined')}</MenuItem>
              <MenuItem value="expired">{t('quotes.expired')}</MenuItem>
            </Select>
          </FormControl>
          {loading && <CircularProgress size={20} />}
        </Box>
      </Paper>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('quotes.quoteNumber')}</TableCell>
              <TableCell>{t('quotes.customer')}</TableCell>
              <TableCell>{t('quotes.address')}</TableCell>
              <TableCell>{t('quotes.type')}</TableCell>
              <TableCell>{t('quotes.status')}</TableCell>
              <TableCell align="center">Site Diagram</TableCell>
              <TableCell align="right">{t('quotes.totalAmount')}</TableCell>
              <TableCell align="right">{t('quotes.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quotes.map((q, idx) => (
              <TableRow key={q.id} hover data-demo-target={idx === 0 ? 'page-quotes-row-first' : undefined}>
                <TableCell>{q.quote_number}</TableCell>
                <TableCell>{q.customer_name}</TableCell>
                <TableCell>
                  {[q.street_address, q.city, q.province].filter(Boolean).join(', ') || '—'}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={q.quote_type_id === 1 ? t('quotes.residential') : 
                          q.quote_type_id === 2 ? t('quotes.commercial') : 
                          q.quote_type_id === 3 ? t('quotes.construction') : 
                          q.quote_type_id ? `${t('quotes.type')} ${q.quote_type_id}` : 'Unknown'}
                    size="small"
                    color={q.quote_type_id === 1 ? 'success' : 
                          q.quote_type_id === 2 ? 'primary' : 
                          q.quote_type_id === 3 ? 'warning' : 'default'}
                    variant="outlined"
                    sx={{ minWidth: '80px' }}
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip 
                      label={t(`quotes.${q.status}`)} 
                      color={getStatusColor(q.status)}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                    {q.envelope_status && (
                      <Chip 
                        label={`DocuSign: ${q.envelope_status}`} 
                        color={getEnvelopeStatusColor(q.envelope_status)} 
                        size="small"
                        icon={<DocuSignIcon />}
                        variant="outlined"
                      />
                    )}
                    {q.esign_status && (
                      <Chip
                        label={t('esign.chip', { status: t(`esign.status.${q.esign_status}`) })}
                        color={esignStatusColor(q.esign_status)}
                        size="small"
                        icon={<EsignIcon />}
                        variant="outlined"
                        onClick={() => setEsignDetailId(q.esign_envelope_id)}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  {q.site_diagram_signed_url ? (
                    <Avatar
                      src={q.site_diagram_signed_url}
                      alt="Site Diagram"
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        cursor: 'pointer',
                        border: '2px solid #e0e0e0',
                        '&:hover': {
                          border: '2px solid #1976d2',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease-in-out'
                      }}
                      onClick={() => handleSiteDiagramClick(q)}
                      title="Click to view site diagram"
                    />
                  ) : (
                    <Box 
                      sx={{ 
                        width: 40, 
                        height: 40, 
                        border: '2px dashed #ccc',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '12px'
                      }}
                    >
                      —
                    </Box>
                  )}
                </TableCell>
                <TableCell align="right">{formatCurrencyWithSymbol(q.total_amount || 0)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={() => handleEdit(q)}
                    title={t('quotes.editQuote')}
                    aria-label={t('quotes.editQuote')}
                    data-demo-target={idx === 0 ? 'page-quotes-row-first-edit' : undefined}
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDuplicateClick(q)}
                    title={t('quotes.duplicateQuote')}
                    aria-label={t('quotes.duplicateQuote')}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                  <IconButton onClick={() => handleApprovalClick(q)} title={t('quotes.manageApproval')} aria-label={t('quotes.manageApproval')}>
                    <ApprovalIcon />
                  </IconButton>
                  <Tooltip 
                    title={
                      pdfGenerating === q.id 
                        ? pdfProgress 
                        : q.has_pdf 
                          ? `PDF exists (${q.latest_pdf_filename || 'Generated'}) - Click to regenerate`
                          : 'Generate PDF'
                    }
                    arrow
                  >
                    <span>
                      <IconButton 
                        onClick={() => handleGeneratePdf(q)} 
                        disabled={pdfGenerating !== null}
                        aria-label={q.has_pdf ? "Regenerate PDF" : "Generate PDF"}
                        sx={{ 
                          color: q.has_pdf ? 'success.main' : 'inherit',
                          '&:hover': {
                            color: q.has_pdf ? 'success.dark' : 'inherit'
                          }
                        }}
                      >
                        {pdfGenerating === q.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <Badge
                            badgeContent={q.has_pdf ? <CheckCircleIcon sx={{ fontSize: 12, color: 'white' }} /> : null}
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            sx={{
                              '& .MuiBadge-badge': {
                                backgroundColor: q.has_pdf ? '#4caf50' : 'transparent',
                                minWidth: 16,
                                height: 16,
                                padding: 0,
                              }
                            }}
                          >
                            <PictureAsPdf />
                          </Badge>
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton onClick={() => handleEmail(q)} title={t('quotes.emailQuote')}>
                    <Send />
                  </IconButton>
                  <IconButton
                    onClick={() => setEsignQuote(q)}
                    title={t('esign.send.button')}
                    aria-label={t('esign.send.button')}
                    color={signingProvider === 'inhouse' ? 'primary' : 'default'}
                  >
                    <EsignIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDocuSignClick(q)} title="Send via DocuSign" color={signingProvider === 'inhouse' ? 'default' : 'primary'}>
                    <DocuSignIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteClick(q)} title={t('quotes.deleteQuote') || 'Delete Quote'} color="error">
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      <Dialog
        open={createOpen}
        onClose={(_, reason) => {
          if (keepQuoteDialogOpen(reason)) return;
          handleCreateCancel();
        }}
        fullWidth
        maxWidth={selectedQuoteType ? "lg" : "md"}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, pr: 1 }}>
          <Box component="span">
            {!selectedQuoteType ? t('quotes.createQuote') : 
             selectedQuoteType === 'residential' ? t('quotes.createResidentialQuote') :
             selectedQuoteType === 'commercial' ? t('quotes.createCommercialQuote') :
             t('quotes.createConstructionQuote')}
          </Box>
          <IconButton
            onClick={handleCreateCancel}
            aria-label={t('common.close')}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!selectedQuoteType ? (
            <QuoteTypeSelector onSelect={handleQuoteTypeSelect} />
          ) : selectedQuoteType === 'construction' ? (
            <QuoteForm onSuccess={handleCreateSuccess} onCancel={handleCreateCancel} />
          ) : (
            <UniversalQuoteForm 
              quoteType={selectedQuoteType}
              onSuccess={handleCreateSuccess} 
              onCancel={handleCreateCancel} 
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!editQuote}
        onClose={(_, reason) => {
          if (keepQuoteDialogOpen(reason)) return;
          setEditQuote(null);
        }}
        fullWidth
        maxWidth={editQuote?.quote_type_id === 1 || editQuote?.quote_type_id === 2 ? "lg" : "md"}
        PaperProps={{ 'data-demo-target': 'page-quotes-detail-dialog' }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, pr: 1 }}>
          <Box>
            <Typography component="span" variant="h6">
              {editQuote?.quote_type_id === 1 ? t('quotes.editResidentialQuote') :
               editQuote?.quote_type_id === 2 ? t('quotes.editCommercialQuote') :
               editQuote?.quote_type_id === 3 ? t('quotes.editConstructionQuote') :
               t('quotes.editQuote')}
            </Typography>
            {editQuote && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.75 }}>
                {editQuote.quote_number && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('quotes.quoteNumberValue', { quoteNumber: editQuote.quote_number })}
                  />
                )}
                <Chip
                  size="small"
                  variant="outlined"
                  label={t('quotes.quoteIdValue', { id: editQuote.id })}
                />
              </Box>
            )}
          </Box>
          <IconButton
            onClick={() => setEditQuote(null)}
            aria-label={t('common.close')}
            data-demo-target="page-quotes-detail-close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {editQuote && (
            editQuote.quote_type_id === 3 ? (
              <QuoteForm 
                quote={editQuote} 
                onSuccess={() => { setEditQuote(null); fetchQuotes(); }} 
                onCancel={() => setEditQuote(null)} 
              />
            ) : (
              <UniversalQuoteForm 
                quote={editQuote}
                quoteType={editQuote.quote_type_id === 1 ? 'residential' : 'commercial'}
                onSuccess={() => { setEditQuote(null); fetchQuotes(); }} 
                onCancel={() => setEditQuote(null)} 
              />
            )
          )}
        </DialogContent>
      </Dialog>

      {/* Quote Approval Dialog */}
      <QuoteApprovalDialog
        open={approvalDialogOpen}
        onClose={() => setApprovalDialogOpen(false)}
        quote={selectedQuote}
        onSuccess={handleApprovalSuccess}
        userRole={user?.user_type}
      />

      {/* Site Diagram Modal */}
      <Dialog 
        open={siteDiagramModalOpen} 
        onClose={() => setSiteDiagramModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Site Diagram - {selectedSiteDiagram?.quoteNumber}
        </DialogTitle>
        <DialogContent>
          {selectedSiteDiagram && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <img
                src={selectedSiteDiagram.url}
                alt={selectedSiteDiagram.filename}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {selectedSiteDiagram.filename}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSiteDiagramModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* DocuSign Send Dialog */}
      <Dialog 
        open={docuSignDialogOpen} 
        onClose={() => !docuSignLoading && setDocuSignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocuSignIcon color="primary" />
            Send Quote via DocuSign
          </Box>
        </DialogTitle>
        <DialogContent>
          {docuSignQuote && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Quote:</strong> {docuSignQuote.quote_number}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Customer:</strong> {docuSignQuote.customer_name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Amount:</strong> {formatCurrencyWithSymbol(docuSignQuote.total_amount || 0)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 2 }}>
                This quote will be sent to the customer via DocuSign for electronic signature. 
                The customer will receive an email with a link to review and sign the document.
              </Typography>
              <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Note:</strong> Make sure a PDF has been generated for this quote before sending. 
                  The quote will be sent to the customer's email address on file.
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDocuSignDialogOpen(false)}
            disabled={docuSignLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDocuSignSend}
            variant="contained"
            color="primary"
            disabled={docuSignLoading}
            startIcon={docuSignLoading ? <CircularProgress size={20} /> : <DocuSignIcon />}
          >
            {docuSignLoading ? 'Sending...' : 'Send via DocuSign'}
          </Button>
        </DialogActions>
      </Dialog>

      <SendForSignatureDialog
        open={!!esignQuote}
        quote={esignQuote}
        onClose={() => setEsignQuote(null)}
        onSent={handleEsignSent}
      />
      <EnvelopeDetailDialog
        envelopeId={esignDetailId}
        open={!!esignDetailId}
        onClose={() => setEsignDetailId(null)}
        onChanged={fetchQuotes}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            {t('quotes.deleteQuote') || 'Delete Quote'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {quoteToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Are you sure you want to delete quote <strong>{quoteToDelete.quote_number}</strong>?
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                This action cannot be undone. All associated items, service configurations, and PDFs will also be deleted.
              </Typography>
              {quoteToDelete.customer_name && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>Customer:</strong> {quoteToDelete.customer_name}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Confirmation Dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={handleDuplicateCancel}
        maxWidth="sm"
        fullWidth
        aria-labelledby="duplicate-quote-dialog-title"
      >
        <DialogTitle id="duplicate-quote-dialog-title">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContentCopyIcon color="primary" />
            {t('quotes.duplicateQuote')}
          </Box>
        </DialogTitle>
        <DialogContent>
          {quoteToDuplicate && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                {t('quotes.duplicateConfirmation', { quoteNumber: quoteToDuplicate.quote_number })}
              </Typography>
              {quoteToDuplicate.customer_name && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{t('quotes.customer')}:</strong> {quoteToDuplicate.customer_name}
                </Typography>
              )}
              {quoteToDuplicate.total_amount != null && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <strong>{t('quotes.totalAmount')}:</strong> {formatCurrencyWithSymbol(quoteToDuplicate.total_amount || 0)}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDuplicateCancel} disabled={duplicating} aria-label={t('common.cancel')}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDuplicateConfirm}
            color="primary"
            variant="contained"
            disabled={duplicating}
            startIcon={duplicating ? <CircularProgress size={20} /> : <ContentCopyIcon />}
            aria-label={t('quotes.duplicate')}
          >
            {duplicating ? t('quotes.duplicating') : t('quotes.duplicate')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Quote: confirm the recipient, then generate and send */}
      <Dialog
        open={emailDialog.open}
        onClose={() => !emailDialog.sending && setEmailDialog({ open: false, quote: null, to: '', sending: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('quotes.emailDialog.title', { number: emailDialog.quote?.quote_number || '' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('quotes.emailDialog.intro')}
          </Typography>
          <TextField
            fullWidth
            type="email"
            label={t('quotes.emailDialog.recipient')}
            value={emailDialog.to}
            onChange={(e) => setEmailDialog((prev) => ({ ...prev, to: e.target.value }))}
            disabled={emailDialog.sending}
            autoFocus
          />
          {emailDialog.sending && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {pdfProgress || t('quotes.emailDialog.sending')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEmailDialog({ open: false, quote: null, to: '', sending: false })}
            disabled={emailDialog.sending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={emailDialog.sending || !emailDialog.to.trim()}
            startIcon={emailDialog.sending ? <CircularProgress size={20} /> : <Send />}
          >
            {emailDialog.sending ? t('quotes.emailDialog.sending') : t('quotes.emailDialog.send')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuoteList;


